import React, { useState, useEffect } from "react";
import { Plus, Trash2, MessageCircle, AlertTriangle, CheckCircle, Clock, ChevronDown, ChevronUp, Bell } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import api from "../utils/api";
import { formatMXN, formatDate, statusColors, statusLabels, categoryLabels } from "../utils/format";

function PurchaseForm({ onClose, onSave }) {
  const [form, setForm] = useState({
    supplier_id: "", supplier_name: "", items: [],
    subtotal: 0, tax_amount: 0, total: 0, notes: "",
    status: "recibida", invoice_number: "", payment_due_date: ""
  });
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/suppliers").then((r) => setSuppliers(r.data)),
      api.get("/products").then((r) => setProducts(r.data)),
    ]);
  }, []);

  const addItem = () => {
    setForm((f) => ({
      ...f, items: [...f.items, { product_id: "", product_name: "", quantity: 1, unit: "kg", unit_cost: 0, subtotal: 0, pieces_received: 0, sell_unit: "" }]
    }));
  };

  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    if (field === "quantity" || field === "unit_cost") {
      items[idx].subtotal = (parseFloat(items[idx].quantity) || 0) * (parseFloat(items[idx].unit_cost) || 0);
    }
    if (field === "product_id") {
      const prod = products.find((p) => p.id === value);
      if (prod) {
        items[idx].product_name = prod.name;
        items[idx].unit = prod.purchase_unit || prod.unit;
        items[idx].sell_unit = prod.unit;
        items[idx].pieces_per_kg = prod.pieces_per_kg || 0;
        items[idx].category = prod.category;
        items[idx].stock = prod.stock || 0;
      }
    }
    const subtotal = items.reduce((a, i) => a + (i.subtotal || 0), 0);
    setForm((f) => ({ ...f, items, subtotal, total: subtotal + f.tax_amount }));
  };

  const handleSave = async () => {
    if (form.items.length === 0) { toast.error("Agrega al menos un artículo"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.payment_due_date) delete payload.payment_due_date;
      await api.post("/purchases", payload);
      toast.success("Compra registrada e inventario actualizado");
      onSave();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const selectSupplier = (id) => {
    const sup = suppliers.find((s) => s.id === id);
    if (sup) setForm((f) => ({ ...f, supplier_id: sup.id, supplier_name: sup.name }));
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="purchase-form">
        <DialogHeader><DialogTitle>Nueva Compra</DialogTitle></DialogHeader>
        <div className="space-y-5 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Proveedor</label>
              <Select value={form.supplier_id} onValueChange={selectSupplier}>
                <SelectTrigger><SelectValue placeholder="Seleccionar proveedor" /></SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">No. de factura del proveedor</label>
              <input value={form.invoice_number} onChange={(e) => setForm((f) => ({ ...f, invoice_number: e.target.value }))}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" placeholder="Opcional" />
            </div>
          </div>

          <div>
            <label className="block text-xs text-slate-500 mb-1">Fecha límite de pago al proveedor</label>
            <input type="date" value={form.payment_due_date}
              onChange={(e) => setForm((f) => ({ ...f, payment_due_date: e.target.value }))}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
            <p className="text-xs text-slate-400 mt-1">El ERP te enviará recordatorios 2 días, 1 día y 12 horas antes del vencimiento.</p>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-600">Artículos comprados</span>
              <button onClick={addItem} className="text-xs text-brand-600 hover:text-brand-700 font-medium">+ Agregar</button>
            </div>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs text-slate-500">Producto</th>
                    <th className="text-left px-3 py-2 text-xs text-slate-500 w-24">Cantidad</th>
                    <th className="text-left px-3 py-2 text-xs text-slate-500 w-16">Unidad</th>
                    <th className="text-left px-3 py-2 text-xs text-slate-500 w-24">Piezas</th>
                    <th className="text-left px-3 py-2 text-xs text-slate-500 w-28">Costo/unidad</th>
                    <th className="text-left px-3 py-2 text-xs text-slate-500 w-28">Subtotal</th>
                    <th className="px-3 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((item, idx) => {
                    const prod = products.find((p) => p.id === item.product_id);
                    const showPieces = prod && prod.unit === "pza" && item.unit === "kg";
                    return (
                    <tr key={idx} className="border-t border-slate-100">
                      <td className="px-3 py-2">
                        <Select value={item.product_id} onValueChange={(v) => updateItem(idx, "product_id", v)}>
                          <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Producto..." /></SelectTrigger>
                          <SelectContent>
                            {products.map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name} <span className="text-slate-400 ml-1">({categoryLabels[p.category] || p.category}) - Stock: {p.stock || 0} {p.unit}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {prod && <div className="text-[10px] text-slate-400 mt-0.5">Stock actual: {prod.stock || 0} {prod.unit}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" value={item.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                          className="w-full border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" min="0" step="0.01" />
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">{item.unit}</td>
                      <td className="px-3 py-2">
                        {showPieces ? (
                          <input type="number" value={item.pieces_received || ""} placeholder="# pzas"
                            onChange={(e) => updateItem(idx, "pieces_received", Number(e.target.value))}
                            data-testid={`pieces-received-${idx}`}
                            className="w-full border border-amber-300 bg-amber-50 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-amber-500" min="0" />
                        ) : (
                          <span className="text-xs text-slate-300">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" value={item.unit_cost} onChange={(e) => updateItem(idx, "unit_cost", e.target.value)}
                          className="w-full border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" min="0" step="0.01" />
                      </td>
                      <td className="px-3 py-2 text-xs font-medium">{formatMXN(item.subtotal)}</td>
                      <td className="px-3 py-2">
                        <button onClick={() => setForm((f) => {
                          const items = f.items.filter((_, i) => i !== idx);
                          return { ...f, items, subtotal: items.reduce((a, i) => a + i.subtotal, 0) };
                        })} className="text-red-400 hover:text-red-600">x</button>
                      </td>
                    </tr>
                    );
                  })}
                  {form.items.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-400 text-xs">Sin articulos</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Notas</label>
              <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                rows={3} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Subtotal</span>
                <span className="font-medium">{formatMXN(form.subtotal)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">IVA ($)</span>
                <input type="number" value={form.tax_amount}
                  onChange={(e) => setForm((f) => ({ ...f, tax_amount: Number(e.target.value), total: f.subtotal + Number(e.target.value) }))}
                  className="w-24 border border-slate-200 rounded px-2 py-0.5 text-xs text-right focus:outline-none focus:ring-1 focus:ring-brand-500" min="0" step="0.01" />
              </div>
              <div className="flex justify-between font-bold text-base border-t border-slate-200 pt-2">
                <span>Total</span>
                <span className="text-brand-700">{formatMXN(form.total)}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Cancelar</button>
          <button onClick={handleSave} disabled={saving} data-testid="save-purchase-btn"
            className="px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const URGENCY_CONFIG = {
  vencida: { label: "VENCIDA", bg: "bg-red-100 border-red-300", text: "text-red-700", icon: AlertTriangle },
  "12h": { label: "Vence en 12h", bg: "bg-red-50 border-red-200", text: "text-red-600", icon: Clock },
  "1d": { label: "Vence mañana", bg: "bg-amber-50 border-amber-200", text: "text-amber-700", icon: AlertTriangle },
  "2d": { label: "Vence en 2 días", bg: "bg-yellow-50 border-yellow-200", text: "text-yellow-700", icon: Bell },
};

export default function Compras() {
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [dueAlerts, setDueAlerts] = useState([]);
  const [bySupplier, setBySupplier] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showBySupplier, setShowBySupplier] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pr, sr, alerts, bySup] = await Promise.all([
        api.get("/purchases"),
        api.get("/suppliers"),
        api.get("/purchases/due-alerts"),
        api.get("/purchases/by-supplier"),
      ]);
      setPurchases(pr.data); setSuppliers(sr.data);
      setDueAlerts(alerts.data); setBySupplier(bySup.data);
    } catch { toast.error("Error al cargar compras"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta compra?")) return;
    await api.delete(`/purchases/${id}`);
    toast.success("Compra eliminada");
    load();
  };

  const handleMarkPaid = async (id) => {
    try {
      await api.post(`/purchases/${id}/mark-paid`);
      toast.success("Compra marcada como pagada");
      load();
    } catch { toast.error("Error al marcar como pagada"); }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const r = await api.post("/purchases/send-due-reminders");
      toast.success(`${r.data.sent} recordatorio(s) enviado(s) a tu correo`);
    } catch { toast.error("Error al enviar recordatorios"); }
    finally { setSendingReminders(false); }
  };

  const handleWhatsAppSupplier = (purchase) => {
    const sup = suppliers.find((s) => s.id === purchase.supplier_id);
    const phone = ((sup?.phone) || "").replace(/\D/g, "");
    if (!phone) { toast.error("Este proveedor no tiene teléfono registrado"); return; }
    const msg = encodeURIComponent(`Hola, adjunto la orden de compra ${purchase.folio} por $${purchase.total?.toFixed(2)} MXN. Favor de confirmar.`);
    const wa = phone.startsWith("52") ? phone : `52${phone}`;
    window.open(`https://web.whatsapp.com/send?phone=${wa}&text=${msg}`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="compras-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Compras</h1>
          <p className="text-slate-500 text-sm mt-1">{purchases.length} compras registradas</p>
        </div>
        <div className="flex gap-2">
          {dueAlerts.length > 0 && (
            <button onClick={handleSendReminders} disabled={sendingReminders}
              data-testid="send-reminders-btn"
              className="flex items-center gap-1.5 text-sm border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 px-3 py-2 rounded-lg transition-colors">
              <Bell size={14} /> {sendingReminders ? "Enviando..." : `Enviar recordatorios (${dueAlerts.length})`}
            </button>
          )}
          <button onClick={() => setShowForm(true)} data-testid="nueva-compra-btn"
            className="flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
            <Plus size={16} /> Nueva compra
          </button>
        </div>
      </div>

      {/* Due Alerts */}
      {dueAlerts.length > 0 && (
        <div className="space-y-2" data-testid="due-alerts">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-500" /> Pagos pendientes a proveedores
          </h2>
          {dueAlerts.map((a) => {
            const cfg = URGENCY_CONFIG[a.urgency] || URGENCY_CONFIG["2d"];
            return (
              <div key={a.id} className={`flex items-center justify-between px-4 py-3 rounded-lg border ${cfg.bg}`}>
                <div className="flex items-center gap-3">
                  <cfg.icon size={15} className={cfg.text} />
                  <div>
                    <span className="font-semibold text-slate-800 text-sm">{a.supplier_name}</span>
                    <span className="text-xs text-slate-500 ml-2">{a.folio}</span>
                  </div>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text} border ${cfg.bg.replace("bg-", "border-")}`}>
                    {cfg.label}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-bold text-slate-900">{formatMXN(a.total)}</span>
                  <span className="text-xs text-slate-500">Vence: {a.payment_due_date?.slice(0, 10)}</span>
                  <button onClick={() => handleMarkPaid(a.id)}
                    className="flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded-md transition-colors">
                    <CheckCircle size={12} /> Marcar pagada
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* By Supplier Summary */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <button onClick={() => setShowBySupplier(!showBySupplier)}
          className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors rounded-xl">
          <span className="text-sm font-semibold text-slate-700">Resumen por proveedor</span>
          {showBySupplier ? <ChevronUp size={15} className="text-slate-400" /> : <ChevronDown size={15} className="text-slate-400" />}
        </button>
        {showBySupplier && (
          <div className="px-5 pb-4 border-t border-slate-100">
            <div className="space-y-3 mt-3">
              {bySupplier.map((s) => (
                <div key={s.supplier_id} className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-slate-800">{s.supplier_name}</span>
                    <div className="text-right">
                      <div className="text-sm font-bold text-slate-900">{formatMXN(s.total_amount)}</div>
                      <div className="text-xs text-slate-500">{s.total_purchases} compras</div>
                    </div>
                  </div>
                  {s.items_summary?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {s.items_summary.slice(0, 5).map((item) => (
                        <span key={item.product_name} className="text-xs bg-white border border-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                          {item.product_name}: {item.total_qty} {item.unit}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {bySupplier.length === 0 && <p className="text-sm text-slate-400">Sin compras registradas</p>}
            </div>
          </div>
        )}
      </div>

      {/* Purchases table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="purchases-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["Folio", "Proveedor", "Fecha", "Vence", "Total", "Estado", "Acciones"].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-12 text-slate-400">Cargando...</td></tr>
            ) : purchases.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-slate-400">Sin compras</td></tr>
            ) : purchases.map((p) => {
              const isOverdue = p.payment_due_date && new Date(p.payment_due_date) < new Date() && p.status !== "pagada";
              return (
                <tr key={p.id} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${isOverdue ? "bg-red-50" : ""}`}>
                  <td className="px-4 py-3 font-mono text-xs font-medium">{p.folio}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{p.supplier_name || <span className="text-slate-400 italic">Sin proveedor</span>}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(p.created_at)}</td>
                  <td className="px-4 py-3 text-xs">
                    {p.payment_due_date ? (
                      <span className={isOverdue ? "text-red-600 font-semibold" : "text-slate-600"}>
                        {p.payment_due_date.slice(0, 10)}
                        {isOverdue && " (Vencida)"}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{formatMXN(p.total)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[p.status] || "bg-slate-100"}`}>
                      {p.status === "pagada" ? "Pagada" : (statusLabels[p.status] || p.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {p.status !== "pagada" && (
                        <button onClick={() => handleMarkPaid(p.id)} title="Marcar como pagada"
                          className="p-1.5 hover:bg-emerald-50 rounded-md transition-colors">
                          <CheckCircle size={14} className="text-emerald-500" />
                        </button>
                      )}
                      <button onClick={() => handleWhatsAppSupplier(p)}
                        className="p-1.5 hover:bg-green-50 rounded-md transition-colors" title="WhatsApp proveedor">
                        <MessageCircle size={14} className="text-green-600" />
                      </button>
                      <button onClick={() => handleDelete(p.id)} className="p-1.5 hover:bg-red-50 rounded-md transition-colors">
                        <Trash2 size={14} className="text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showForm && <PurchaseForm onClose={() => setShowForm(false)} onSave={() => { setShowForm(false); load(); }} />}
    </div>
  );
}
